import { parseCanonicalIcd10Code } from "@smartmed/codes";

export type AgeLimits = {
  minAge: number | null;
  maxAge: number | null;
};

export type AgeMismatch = AgeLimits & {
  patientAge: number;
};

export const isRecordedAge = (age: number | null | undefined): age is number =>
  typeof age === "number" && Number.isInteger(age) && age >= 0;

export function ageMismatch(
  limits: Partial<AgeLimits> | null | undefined,
  patientAge: number | null | undefined,
): AgeMismatch | null {
  if (!isRecordedAge(patientAge)) {
    return null;
  }
  const minAge = limits?.minAge ?? null;
  const maxAge = limits?.maxAge ?? null;
  const belowMin = minAge !== null && patientAge < minAge;
  const aboveMax = maxAge !== null && patientAge > maxAge;
  if (!belowMin && !aboveMax) {
    return null;
  }
  return { minAge, maxAge, patientAge };
}

export const ageCanRemoveCodes = (patientAge: number | null | undefined): boolean => patientAge === 0;

/** Looks each item up by its canonical ICD-10 code, falling back to the code as given. */
export function splitByAgeFit<T extends { code: string }>(
  items: readonly T[],
  limitsByCode: ReadonlyMap<string, Partial<AgeLimits>>,
  patientAge: number | null | undefined,
): { fitting: T[]; mismatched: { item: T; mismatch: AgeMismatch }[] } {
  const fitting: T[] = [];
  const mismatched: { item: T; mismatch: AgeMismatch }[] = [];
  for (const item of items) {
    const mismatch = ageMismatch(limitsByCode.get(parseCanonicalIcd10Code(item.code) ?? item.code), patientAge);
    if (mismatch) {
      mismatched.push({ item, mismatch });
    } else {
      fitting.push(item);
    }
  }
  return { fitting, mismatched };
}

export function ageMismatchText({ minAge, maxAge, patientAge }: AgeMismatch): string {
  if (minAge !== null && maxAge !== null) {
    return `Usual age is ${minAge}–${maxAge} but patient aged ${patientAge}`;
  }
  if (minAge !== null) {
    return `Usual age is ${minAge} or over but patient aged ${patientAge}`;
  }
  return `Usual age is ${maxAge} or under but patient aged ${patientAge}`;
}
